import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
	ArrowLeft,
	LifeBuoy,
	Pin,
	PinOff,
	Search,
	Settings,
	Tag,
	ThumbsDown,
	ThumbsUp,
	X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { ScopePicker, scopeLabel, useScopeWithTree } from "@/components/scope-picker";
import { pickDefaultScope } from "@/components/scope-picker/scope-sanity";
import type { ScopeRef } from "@/components/scope-picker/types";
import { MobileFilterField, MobileFilters } from "@/components/ui/mobile-filters";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
	uniqueLabelled,
	VirtualizedStringCombobox,
} from "@/components/ui/virtualized-string-combobox";
import type { MarginBands } from "@/features/asystent/asystent-api";
import {
	asystentPharmacies,
	OPEN_STATION_REPORT_EVENT,
	panelStationReport,
	sendAsystentFeedback,
} from "@/features/asystent/asystent-api";
import {
	AsystentRowDetails,
	DETAIL_CACHE,
	LeafletWindowView,
} from "@/features/asystent/components/asystent-row-details";
import {
	MarginBadge,
	marginLevel,
	NrtowChip,
	pickSignals,
	RodzajBadge,
	SignalChip,
} from "@/features/asystent/components/asystent-signals";
import { polishCount } from "@/features/asystent/components/mobile-stats";
import { OpeningSettings } from "@/features/asystent/components/opening-settings";
import { ResultOrderSettings } from "@/features/asystent/components/result-order-settings";
import { useAsystentSearch } from "@/features/asystent/hooks/use-asystent-search";
import { useMarginBands } from "@/features/asystent/hooks/use-margin-bands";
import type { AsystentItem } from "@/features/asystent/types";
import { useAuth } from "@/features/auth/use-auth";
import { ProductSearchBar } from "@/features/products/components/product-search-bar";
import { productsApi } from "@/features/products/products-api";
import { effectiveFinalPriceFromItem } from "@/features/strateg-cenowy/lib/effective-price";
import { useIsBelowMd } from "@/hooks/use-mobile";
import { panelEmit, readEmbedPanel } from "@/lib/auth-api";
import { formatStock } from "@/lib/format";
import { ensureElementVisible } from "@/lib/route-guards";
import { encodeScope } from "@/lib/scope";
import { useDebounce } from "@/lib/use-debounce";
import { cn } from "@/lib/utils";

/// Uchwyt do bieżącego okna Tauri (panel WebView). `null` poza panelem (przeglądarka).
/// Używamy WBUDOWANYCH komend okna (core:*) — działają dla zdalnej strony przez
/// capability `asystent-panel-remote`; custom commands ACL blokuje dla remote.
function currentPanelWindow(): {
	hide: () => Promise<void>;
	startDragging: () => Promise<void>;
	startResizeDragging: (dir: string) => Promise<void>;
	setTitle: (title: string) => Promise<void>;
} | null {
	const t = (window as unknown as { __TAURI__?: { window?: { getCurrentWindow?: () => unknown } } })
		.__TAURI__;
	const w = t?.window?.getCurrentWindow?.();
	return (w as ReturnType<typeof currentPanelWindow>) ?? null;
}

/// Pin panelu sygnalizujemy agentowi przez TYTUŁ okna (wbudowane setTitle, dozwolone
/// dla zdalnej strony; custom command pin_panel ACL blokuje dla remote). Agent czyta
/// sufiks w pętli auto-hide: z sufiksem = nie chowaj. Sufiks MUSI zgadzać się z
/// `PIN_TITLE_SUFFIX` w panel_window.rs ("•pin"). Niewidoczny (okno bezramkowe).
const PANEL_TITLE_BASE = "Asystent Farmaceuty";
const PANEL_PIN_SUFFIX = "•pin";
function setPanelPinned(pinned: boolean): void {
	try {
		void currentPanelWindow()?.setTitle(
			pinned ? PANEL_TITLE_BASE + PANEL_PIN_SUFFIX : PANEL_TITLE_BASE,
		);
	} catch (e) {
		console.warn("[panel] pin failed:", e);
	}
}

/// Zamknij panel (X) = UKRYJ okno (nie close/destroy). Ukrycie zachowuje pozycję i
/// rozmiar — ponowne otwarcie (tray/krawędź) pokazuje to samo okno w tym samym
/// miejscu. `close()` niszczył okno i odtwarzał je z domyślnym małym rozmiarem w
/// losowym miejscu (regresja). Poza panelem = no-op.
function hidePanelWindow(): void {
	try {
		void currentPanelWindow()?.hide();
	} catch (e) {
		console.warn("[panel] hide failed:", e);
	}
}

/// Rozpocznij natywny resize okna z uchwytu krawędzi/rogu (bezramkowe okno nie ma
/// natywnych krawędzi). Kierunki Tauri: "West"/"South"/"SouthWest" itd.
function startPanelResize(dir: string): void {
	try {
		void currentPanelWindow()?.startResizeDragging(dir);
	} catch (e) {
		console.warn("[panel] resize failed:", e);
	}
}

const searchSchema = z.object({
	// coerce: skan kodu kreskowego daje czysto-cyfrowe `q` → TanStack parsuje je jako
	// number; coerce.string() chroni przed crashem walidacji (jak `leaflet` niżej).
	q: z.coerce.string().optional(),
	scope: z.string().optional(),
	// `stockOnly` domyslnie true (pokazujemy tylko produkty ze stanem). Toggle
	// "Pokaż też braki" ustawia stockOnly=false; serializujemy jako boolean w URL.
	stockOnly: z.boolean().optional().catch(undefined),
	// `leaflet=<bloz07>`: tryb OSOBNEGO okna ulotki (agent otwiera /asystent?leaflet=...
	// jako wieksze, wysrodkowane okno). Renderujemy wtedy tylko widok PDF, bez wyszukiwarki.
	// coerce: TanStack Router parsuje czysto-cyfrowy param jako number (np. 6067222) —
	// coerce.string() przyjmuje number|string. Wiodace zera kodu odtwarzamy padem (7 cyfr).
	// `.catch(undefined)` — FARMASTER-FRONTEND-31: zły param w URL nie może wywalić trasy.
	leaflet: z.coerce.string().optional().catch(undefined),
});

type AsystentSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_auth/asystent")({
	beforeLoad: async ({ context, location }) => {
		await ensureElementVisible(
			context.queryClient,
			"sidebar.analityka.asystent",
			location.pathname,
		);
	},
	validateSearch: (s: Record<string, unknown>): AsystentSearch => searchSchema.parse(s),
	component: AsystentPage,
});

function AsystentPage() {
	const search = Route.useSearch();
	// Tryb OSOBNEGO okna ulotki: agent otwiera /asystent?leaflet=<bloz07> jako
	// wieksze, wysrodkowane okno → renderujemy TYLKO widok PDF (panel wyszukiwania
	// zyje w swoim osobnym oknie i zostaje nietkniety). Dispatcher trzyma hooki
	// rozdzielne miedzy widokami (rules-of-hooks).
	if (search.leaflet) {
		// kod07 ma 7 cyfr — odtwórz wiodące zera zgubione przez numeryczny parse URL.
		return <LeafletWindowView bloz07={String(search.leaflet).padStart(7, "0")} />;
	}
	return <AsystentSearchPage />;
}

function AsystentSearchPage() {
	const { user } = useAuth();
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const isBelowMd = useIsBelowMd();

	const [searchQuery, setSearchQuery] = useState(search.q ?? "");
	// 600ms (nie 250): każde świeże zapytanie odpala płatny pipeline LLM
	// (strażnik ~7k tokenów). Krótki debounce = osobny koszt za każdą pauzę w
	// pisaniu ("na b" → "na bó" → "na ból głowy" = 3× strażnik za 1 zamiar).
	const debouncedQuery = useDebounce(searchQuery, 600);
	// Aktywny produkt: na wąsko rozwija się inline pod wierszem, na desktopie
	// pokazuje się w prawym panelu (master-detail). Jeden stan steruje oboma.
	const [selected, setSelected] = useState<string | null>(null);
	const queryClient = useQueryClient();
	// Stos kontekstów: klik w zamiennik/cross wpisuje jego nazwę do wyszukiwarki
	// (zmiana leku); "Wróć" zdejmuje ostatni i przywraca poprzednie zapytanie.
	const [history, setHistory] = useState<string[]>([]);

	// Toggle "Pokaż też braki": ON → stockOnly=false (pokazuj tez braki),
	// OFF (default) → stockOnly=true (tylko ze stanem). URL trzyma stockOnly.
	const stockOnly = search.stockOnly ?? true;
	const showBraki = !stockOnly;

	// Embedded (panel agenta): scope PRZYPIĘTY do apteki urządzenia (z device-JWT);
	// backend i tak wymusza scope per apteka, tu spinamy UI (stałe query-keys, bez
	// pickera, bez gating drzewa widoczności które device-JWT może nie mieć).
	const embed = readEmbedPanel();
	const isCanary = embed?.isCanary === true;

	// Canary (laptop bez stałej apteki): picker aptek z backendu przez ZWYKŁY apiFetch
	// (panel-JWT), NIE przez Tauri IPC — zdalna strona nie sięga custom commands agenta
	// (ACL), więc HTTP jest jedyną pewną drogą. Wybór trzymamy lokalnie + w localStorage,
	// żeby reotwarcie panelu pamiętało ostatnią aptekę. Stanowisko apteczne: idfirm z tokenu.
	const [canaryPharmacies, setCanaryPharmacies] = useState<{ idfirm: number; name: string }[]>([]);
	const [canaryIdfirm, setCanaryIdfirm] = useState<number | null>(() => {
		if (typeof window === "undefined") return null;
		const v = window.localStorage.getItem("asystent:canary:idfirm");
		return v ? Number(v) : null;
	});
	useEffect(() => {
		if (!isCanary) return;
		asystentPharmacies()
			.then((list) => {
				if (Array.isArray(list)) setCanaryPharmacies(list);
			})
			.catch((e) => console.warn("[panel] lista aptek:", e));
	}, [isCanary]);
	const canaryOptions = useMemo(
		() =>
			uniqueLabelled(
				canaryPharmacies,
				(p) => p.name,
				(p) => p.idfirm,
			),
		[canaryPharmacies],
	);
	// `null` = „Wyczyść" w pickerze: bez apteki panel prosi o wybór i nie szuka.
	const pickCanary = (idfirm: number | null) => {
		setCanaryIdfirm(idfirm);
		try {
			if (idfirm == null) window.localStorage.removeItem("asystent:canary:idfirm");
			else window.localStorage.setItem("asystent:canary:idfirm", String(idfirm));
		} catch {
			// localStorage może być wyłączony — wybór zostaje tylko w pamięci sesji
		}
	};

	// Pin panelu: wyłącza auto-hide po stronie agenta (sygnał przez tytuł okna).
	const [pinned, setPinned] = useState(false);
	const [openingSettings, setOpeningSettings] = useState(false);
	const openingButton = useRef<HTMLButtonElement>(null);
	const togglePin = () => setPinned((p) => !p);
	// Focus w polu Szukaj też blokuje auto-hide: mysz może wyjechać poza okno w
	// trakcie pisania (np. sięgnąć po opakowanie) — panel nie może wtedy zniknąć.
	const [searchFocused, setSearchFocused] = useState(false);
	const [pickerFocused, setPickerFocused] = useState(false);
	// Jeden sygnał do agenta = pin użytkownika LUB aktywny focus wyszukiwarki.
	useEffect(() => {
		setPanelPinned(pinned || searchFocused || pickerFocused || openingSettings);
	}, [pinned, searchFocused, pickerFocused, openingSettings]);
	// Helper pokazuje świeże okno panelu dopiero teraz, bez białego okna w trakcie ładowania.
	useEffect(() => {
		if (embed) void panelEmit("asystent-panel:ready");
	}, [embed]);
	// „Zgłoś problem” z panelu: serwer decyduje (data startu, Canary, wykluczenia, wersja
	// agenta). Panel wisi ukryty godzinami, więc flaga odświeża się sama — start floty
	// o północy pokaże ikonę bez przeładowania.
	const stationReport = useQuery({
		queryKey: ["asystent", "panel-station-report"],
		queryFn: panelStationReport,
		enabled: !!embed,
		staleTime: 5 * 60_000,
		refetchInterval: 10 * 60_000,
		retry: false,
	});

	const embedIdfirm = embed ? (isCanary ? canaryIdfirm : embed.idfirm) : null;
	const tree = useScopeWithTree({
		persistKey: user?.id ? `scope:asystent:${user.id}` : "scope:asystent:anon",
		urlScope: search.scope,
		fallback: embed ? { type: "apteka", id: embed.idfirm } : { type: "wlasne" },
		onScopeChange: (s) =>
			navigate({
				search: (prev: AsystentSearch) => ({ ...prev, scope: encodeScope(s) }),
				replace: true,
			}),
	});
	const scope: ScopeRef = embed ? { type: "apteka", id: embedIdfirm ?? 0 } : tree.scope;
	const setScope = tree.setScope;
	// Canary bez wybranej apteki → scope NIE gotowy (nie odpalamy wyszukiwania na apteka:0).
	const scopeReady = embed ? (isCanary ? canaryIdfirm != null : true) : tree.scopeReady;
	const encodedScope = encodeScope(scope);
	// Progi band marzy (wysoka/srednia/niska) — pracownik apteki widzi poziom
	// zamiast dokladnego %. Jedno zrodlo dla wiersza + naglowka detalu.
	const marginBands = useMarginBands(encodedScope, scopeReady);

	const setQuery = (q: string) => {
		setSearchQuery(q);
		navigate({
			search: (prev: AsystentSearch) => ({ ...prev, q: q || undefined }),
			replace: true,
		});
	};

	const setShowBraki = (v: boolean) => {
		navigate({
			// v=true → pokazuj braki → stockOnly=false. Default (false) czyscimy z URL.
			search: (prev: AsystentSearch) => ({ ...prev, stockOnly: v ? false : undefined }),
			replace: true,
		});
	};

	// Klik w zamiennik/cross-sell → ten lek staje się nowym kontekstem (wpisany do
	// wyszukiwarki). Bieżące zapytanie ląduje na stosie, żeby dało się cofnąć.
	const pickProduct = (name: string) => {
		setHistory((h) => [...h, searchQuery]);
		setSelected(null);
		setQuery(name);
	};
	const goBack = () => {
		if (history.length === 0) return;
		const prev = history[history.length - 1];
		setHistory((h) => h.slice(0, -1));
		setSelected(null);
		setQuery(prev);
	};

	const { data, searching: searchingRaw } = useAsystentSearch({
		scope,
		q: debouncedQuery,
		stockOnly,
		scopeReady,
	});
	const items = data?.items ?? [];

	// Feedback trafności (łapka góra/dół). Głos per apteka → tylko gdy scope
	// zawężony do JEDNEJ apteki (panel device/canary zawsze; web-user musi wybrać).
	// Backend i tak pilnuje (400), tu gate'ujemy UI, żeby nie kusić przyciskiem.
	const canVote = scope.type === "apteka";
	const feedbackMut = useMutation({
		mutationFn: (v: { kod07: string; positive: boolean }) =>
			sendAsystentFeedback({
				q: debouncedQuery,
				kod07: v.kod07,
				positive: v.positive,
				scope: encodedScope,
			}),
		onSuccess: (_r, v) =>
			toast.success(v.positive ? "Zapisano: produkt pasuje" : "Zapisano: produkt nie pasuje"),
		onError: () => toast.error("Nie udało się zapisać oceny"),
	});

	const tooShort = debouncedQuery.trim().length < 2;
	// Jeden strzał: animacja ładowania do czasu gotowych (przefiltrowanych)
	// wyników — bez podmiany surowych na inne (decyzja produktowa).
	const searching = !tooShort && searchingRaw;

	// Nowe wyszukiwanie / zmiana scope'u czyści zaznaczenie (stary panel byłby nieaktualny).
	// biome-ignore lint/correctness/useExhaustiveDependencies: deps celowo szersze — reset zaznaczenia MA się odpalać na każdą zmianę zapytania/scope'u
	useEffect(() => {
		setSelected(null);
	}, [debouncedQuery, encodedScope]);

	const selectedItem = items.find((i) => i.bloz07 === selected) ?? null;
	const toggle = (bloz: string) => setSelected((curr) => (curr === bloz ? null : bloz));

	// Telefon w przeglądarce (bez panelu agenta): filtry w chipach, ocena trafności
	// w rozwinięciu wiersza (brak hovera). Panel agenta (embed) bywa węższy niż `md`,
	// ale ma własny, sprawdzony układ — zostaje bez zmian.
	const isPhone = isBelowMd && !embed;
	const mobileScopePicker = (
		<ScopePicker value={scope} onChange={setScope} className="h-10 w-full" />
	);
	const mobileBrakiToggle = (
		<label className="flex min-h-10 cursor-pointer select-none items-center gap-2.5 text-sm text-foreground">
			<input
				type="checkbox"
				className="size-4 accent-[var(--primary)]"
				checked={showBraki}
				onChange={(e) => setShowBraki(e.target.checked)}
			/>
			Pokaż też braki
		</label>
	);
	// Chipy liczone tylko na telefonie — na desktopie MobileFilters ich nie renderuje.
	// Domyslny zakres jak w useScopeWithTree: konto z jedna apteka dostaje te apteke.
	const defaultScope: ScopeRef = (tree.tree && pickDefaultScope(tree.tree)) ?? { type: "wlasne" };
	const mobileChips = isPhone
		? [
				{
					id: "scope",
					label: "Zakres aptek",
					value: scopeLabel(scope, tree.tree),
					active: encodedScope !== encodeScope(defaultScope),
					picker: mobileScopePicker,
				},
				{
					id: "stock",
					label: "Stan",
					value: showBraki ? "Też braki" : "Tylko ze stanem",
					active: showBraki,
					picker: mobileBrakiToggle,
				},
			]
		: [];

	// Prefetch na hover — rozgrzewa najcięższe zapytania rozwiniętego wiersza (opis
	// + "Warto dołożyć"), żeby klik był natychmiastowy. Klucze MUSZĄ pasować do tych
	// w AsystentRowDetails; rozjazd = cichy miss (perf), nie błąd. `ensureQueryData`
	// no-op gdy już w cache (DETAIL_CACHE 5 min).
	const prefetchRow = (bloz07: string) => {
		void queryClient.prefetchQuery({
			queryKey: ["asystent-detail", bloz07, encodedScope],
			queryFn: () => productsApi.getDetail(bloz07, { scope: encodedScope }),
			...DETAIL_CACHE,
		});
		void queryClient.prefetchQuery({
			queryKey: ["asystent-complement", bloz07, encodedScope],
			queryFn: () => productsApi.crossSell(bloz07, { scope: encodedScope }),
			...DETAIL_CACHE,
		});
	};

	return (
		// Master-detail: na desktopie lista po lewej (stała 380 px ≈ szerokość docka
		// FarmasterAgent) + panel szczegółów po prawej. Poniżej `lg` panel znika i
		// wiersz rozwija się inline. ScopePicker w prawym-górnym rogu (pasek modułu) —
		// w trybie panelowym FarmasterAgent ten pasek znika, scope przychodzi z hosta.
		<div
			className={cn("flex h-full w-full flex-col overflow-hidden bg-card/30", embed && "relative")}
		>
			{/* Uchwyty resize bezramkowego okna panelu (embed): okno dosunięte do prawej
			    krawędzi ekranu, więc zmieniamy szerokość lewą krawędzią i wysokość dolną
			    (+ róg). Wołają natywne startResizeDragging (core:window:allow-start-resize-dragging). */}
			{embed && (
				<>
					{/* biome-ignore lint/a11y/noStaticElementInteractions: uchwyt natywnego resize okna Tauri (mysz-only, brak odpowiednika klawiaturowego) */}
					<div
						onMouseDown={() => startPanelResize("West")}
						className="absolute left-0 top-0 z-20 h-full w-1.5 cursor-ew-resize"
						title="Zmień szerokość"
					/>
					{/* biome-ignore lint/a11y/noStaticElementInteractions: uchwyt natywnego resize okna Tauri (mysz-only, brak odpowiednika klawiaturowego) */}
					<div
						onMouseDown={() => startPanelResize("South")}
						className="absolute bottom-0 left-0 z-20 h-1.5 w-full cursor-ns-resize"
						title="Zmień wysokość"
					/>
					{/* biome-ignore lint/a11y/noStaticElementInteractions: uchwyt natywnego resize okna Tauri (mysz-only, brak odpowiednika klawiaturowego) */}
					<div
						onMouseDown={() => startPanelResize("SouthWest")}
						className="absolute bottom-0 left-0 z-30 size-3 cursor-nesw-resize"
						title="Zmień rozmiar"
					/>
				</>
			)}

			{/* Web (nie-embedded): pełny pasek z ScopePickerem. Telefon: tytuł jest w
			    pasku górnym, a zakres i „braki” w chipach + arkuszu filtrów. */}
			{!embed && (
				<MobileFilters
					chips={mobileChips}
					sheetContent={
						<>
							<MobileFilterField label="Zakres aptek">{mobileScopePicker}</MobileFilterField>
							<MobileFilterField label="Stan">{mobileBrakiToggle}</MobileFilterField>
						</>
					}
					onClear={() => {
						setScope(defaultScope);
						setShowBraki(false);
					}}
					resultLabel={
						tooShort || searching
							? "Pokaż wyniki"
							: `Pokaż ${polishCount(items.length, "produkt", "produkty", "produktów")}`
					}
				>
					<header className="flex items-center justify-between gap-2 border-b bg-card/50 px-3 py-2">
						<h1 className="truncate text-sm font-semibold text-foreground">Asystent Farmaceuty</h1>
						<ScopePicker value={scope} onChange={setScope} />
					</header>
				</MobileFilters>
			)}

			{/* Embedded (panel agenta): slim pasek — nazwa apteki (lub picker dla canary)
			    po lewej + zamknij po prawej. Cały pasek to `data-tauri-drag-region`
			    (przeciąganie bezramkowego okna); select/przycisk to interaktywne dzieci,
			    więc klik w nie NIE przeciąga. Panel jest „zawsze przypięty" po stronie
			    agenta (bez auto-hide), więc przycisk pin jest zbędny. */}
			{embed && (
				<header
					data-tauri-drag-region
					className="flex items-center justify-between gap-2 border-b bg-card/50 px-2 py-1.5"
				>
					<div data-tauri-drag-region className="flex min-w-0 items-center gap-1.5">
						<Tooltip>
							<TooltipTrigger asChild>
								<button
									ref={openingButton}
									type="button"
									aria-label="Ustawienia"
									aria-expanded={openingSettings}
									onClick={() => setOpeningSettings((open) => !open)}
									className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
								>
									<Settings className="size-4" />
								</button>
							</TooltipTrigger>
							<TooltipContent>Ustawienia</TooltipContent>
						</Tooltip>
						{isCanary ? (
							// Canary (laptop KR) przełącza się między ~300 aptekami tenanta —
							// natywny select bez wyszukiwania był nie do przewinięcia. Focus
							// w pickerze (także w polu szukania popovera, portal React) pinuje
							// panel jak pole Szukaj, żeby nie schował się w trakcie pisania.
							<fieldset
								aria-label="Apteka"
								className="m-0 min-w-0 border-0 p-0"
								onFocus={() => setPickerFocused(true)}
								onBlur={() => setPickerFocused(false)}
							>
								<VirtualizedStringCombobox
									options={canaryOptions.labels}
									value={
										canaryIdfirm == null ? "" : (canaryOptions.idToLabel.get(canaryIdfirm) ?? "")
									}
									onChange={(label) => pickCanary(canaryOptions.labelToId.get(label) ?? null)}
									placeholder="Wybierz aptekę…"
									searchPlaceholder="Szukaj apteki…"
									className="h-7 max-w-[220px] px-1.5 text-xs font-medium"
								/>
							</fieldset>
						) : (
							<h1 data-tauri-drag-region className="truncate text-sm font-semibold text-foreground">
								{embed.pharmacyName ?? "Asystent Farmaceuty"}
							</h1>
						)}
					</div>
					<div className="flex shrink-0 items-center gap-0.5">
						{stationReport.data?.enabled && (
							<Tooltip>
								<TooltipTrigger asChild>
									<button
										type="button"
										aria-label="Zgłoś problem do działu IT"
										onClick={() => void panelEmit(OPEN_STATION_REPORT_EVENT)}
										className="inline-flex size-7 items-center justify-center rounded-md text-status-success-text transition-colors hover:bg-status-success-bg"
									>
										<LifeBuoy className="size-4" />
									</button>
								</TooltipTrigger>
								<TooltipContent>Zgłoś problem do działu IT</TooltipContent>
							</Tooltip>
						)}
						<button
							type="button"
							onClick={togglePin}
							title={pinned ? "Odepnij (auto-chowanie wł.)" : "Przypnij (nie chowaj)"}
							aria-pressed={pinned}
							className={cn(
								"inline-flex size-7 items-center justify-center rounded-md transition-colors hover:bg-muted",
								pinned ? "text-primary" : "text-muted-foreground",
							)}
						>
							{pinned ? <PinOff className="size-4" /> : <Pin className="size-4" />}
						</button>
						<button
							type="button"
							onClick={hidePanelWindow}
							title="Zamknij panel"
							className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
						>
							<X className="size-4" />
						</button>
					</div>
				</header>
			)}

			{embed && openingSettings && (
				<OpeningSettings
					scope={encodedScope}
					pharmacyName={
						isCanary
							? canaryIdfirm == null
								? undefined
								: canaryOptions.idToLabel.get(canaryIdfirm)
							: (embed.pharmacyName ?? undefined)
					}
					onClose={() => {
						setOpeningSettings(false);
						openingButton.current?.focus();
					}}
				/>
			)}
			<div className="flex min-h-0 flex-1 overflow-hidden">
				{/* ── Lewa kolumna: wyszukiwarka + lista ── */}
				<div className="flex min-h-0 w-full flex-col lg:w-[380px] lg:shrink-0 lg:border-r">
					<div className="border-b bg-card/50 px-3 py-3">
						<div className="flex flex-col gap-2">
							{history.length > 0 && (
								<button
									type="button"
									onClick={goBack}
									className={cn(
										"inline-flex items-center gap-1 self-start rounded-md px-1.5 py-0.5 text-xs text-primary transition-colors hover:bg-muted",
										isPhone && "min-h-9 px-2",
									)}
								>
									<ArrowLeft className="size-3.5" aria-hidden />
									Wróć{history[history.length - 1] ? `: ${history[history.length - 1]}` : ""}
								</button>
							)}
							<ProductSearchBar
								value={searchQuery}
								onChange={setQuery}
								onClear={() => setQuery("")}
								onFocus={() => setSearchFocused(true)}
								onBlur={() => setSearchFocused(false)}
								placeholder="Lek, objaw, substancja, nr Kamsoft, BLOZ lub EAN..."
								autoFocus
							/>
							{/* Telefon (web): przełącznik jest w chipie „Stan” i arkuszu filtrów. */}
							<label
								className={cn(
									"flex cursor-pointer select-none items-center justify-end gap-1.5 whitespace-nowrap text-xs",
									isPhone && "hidden",
								)}
							>
								<input
									type="checkbox"
									className="size-3.5 accent-[var(--primary)]"
									checked={showBraki}
									onChange={(e) => setShowBraki(e.target.checked)}
								/>
								<span className="text-muted-foreground">Pokaż też braki</span>
							</label>
							{/* Panel agenta ma kolejność w ustawieniach (koło zębate). */}
							{!embed && (
								<Popover>
									<PopoverTrigger asChild>
										<button
											type="button"
											className="self-end whitespace-nowrap rounded-md px-1.5 py-0.5 text-xs text-primary hover:bg-muted"
										>
											Kolejność wyników
										</button>
									</PopoverTrigger>
									<PopoverContent align="end" className="w-80 text-sm">
										<ResultOrderSettings
											scope={encodedScope}
											pharmacyName={
												scope.type === "apteka" ? scopeLabel(scope, tree.tree) : undefined
											}
										/>
									</PopoverContent>
								</Popover>
							)}
						</div>
					</div>

					<div className="min-h-0 flex-1 overflow-y-auto">
						{isCanary && canaryIdfirm == null ? (
							<Centered>Wybierz aptekę u góry, aby wyszukiwać</Centered>
						) : tooShort ? (
							<Centered>Wpisz co najmniej 2 znaki</Centered>
						) : searching ? (
							<div className="flex h-full flex-col items-center justify-center gap-5 px-6 text-center">
								{/* Kotek szukający: ogon macha, oczy mrugają.
								    Motyw przez currentColor (text-primary), animacja SMIL — działa też
								    w WebView panelu agenta. */}
								<svg
									viewBox="0 0 64 64"
									className="size-24 text-primary"
									fill="none"
									role="img"
									aria-label="Szukam"
								>
									<g
										stroke="currentColor"
										strokeWidth="2.5"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<path d="M22 16 L20 5 L31 13 Z" fill="currentColor" />
										<path d="M42 16 L44 5 L33 13 Z" fill="currentColor" />
										<circle cx="32" cy="26" r="13" />
										<path d="M21 36 C 17 49, 22 58, 32 58 C 42 58, 47 49, 43 36" />
										<g>
											<animateTransform
												attributeName="transform"
												type="rotate"
												values="-7 44 54; 14 44 54; -7 44 54"
												dur="1.5s"
												repeatCount="indefinite"
												calcMode="spline"
												keyTimes="0;0.5;1"
												keySplines="0.45 0 0.55 1; 0.45 0 0.55 1"
											/>
											<path d="M44 54 C 56 53, 59 41, 51 34" />
										</g>
										<path d="M23 28 L12 26 M23 31 L12 32" />
										<path d="M41 28 L52 26 M41 31 L52 32" />
										<path d="M30 30 L34 30 L32 33 Z" fill="currentColor" stroke="none" />
									</g>
									<g fill="currentColor">
										<ellipse cx="27" cy="25" rx="2" ry="2.6">
											<animate
												attributeName="ry"
												values="2.6;2.6;0.3;2.6"
												keyTimes="0;0.9;0.95;1"
												dur="3.4s"
												repeatCount="indefinite"
											/>
										</ellipse>
										<ellipse cx="37" cy="25" rx="2" ry="2.6">
											<animate
												attributeName="ry"
												values="2.6;2.6;0.3;2.6"
												keyTimes="0;0.9;0.95;1"
												dur="3.4s"
												repeatCount="indefinite"
											/>
										</ellipse>
									</g>
								</svg>
								<div className="text-sm font-medium text-muted-foreground">
									Szukam najlepszych produktów… 🐾
								</div>
							</div>
						) : items.length === 0 ? (
							<Centered>Brak wyników</Centered>
						) : (
							<div className="divide-y">
								{items.map((item) => (
									<div
										key={item.bloz07}
										data-asystent-row
										className={cn(
											"border-l-[3px] transition-colors",
											selected === item.bloz07
												? "border-l-primary bg-card shadow-sm"
												: "border-l-transparent",
										)}
									>
										{/* Box tylko-wiersza: klaster łapek kotwiczy do jego dołu (nie
										    do dołu rozwinięcia); `group` steruje ujawnianiem na hover. */}
										<div className="group relative">
											<AsystentRow
												item={item}
												selected={selected === item.bloz07}
												onClick={() => toggle(item.bloz07)}
												onHover={() => prefetchRow(item.bloz07)}
												bands={marginBands}
											/>
											{canVote && !isPhone && (
												<FeedbackCluster
													busy={feedbackMut.isPending}
													pinned={selected === item.bloz07}
													onVote={(positive) =>
														feedbackMut.mutate({ kod07: item.bloz07, positive })
													}
												/>
											)}
										</div>
										{/* Inline expand — tylko na wąsko (na desktopie szczegóły w prawym panelu).
										    Ocena zostaje w wierszu (FeedbackCluster pinned) — bez dublowania tutaj.
										    Telefon (web): bez hovera łapki są tutaj, jako przyciski 36 px. */}
										{selected === item.bloz07 && (
											<div
												data-testid="asystent-expansion"
												className="border-t border-border bg-muted/40 px-3 pb-3 pt-2 text-xs lg:hidden"
											>
												<p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-primary/70">
													Szczegóły — {item.nazwa}
												</p>
												{isPhone && canVote && (
													<MobileFeedbackRow
														busy={feedbackMut.isPending}
														onVote={(positive) =>
															feedbackMut.mutate({ kod07: item.bloz07, positive })
														}
													/>
												)}
												<AsystentRowDetails
													bloz07={item.bloz07}
													primaryCategoryCode={item.primaryCategoryCode}
													primaryCategoryLabel={item.primaryCategoryLabel}
													scope={scope}
													onPickProduct={pickProduct}
													onCollapse={() => toggle(item.bloz07)}
												/>
											</div>
										)}
									</div>
								))}
							</div>
						)}
					</div>
				</div>

				{/* ── Prawa kolumna: panel szczegółów (tylko desktop) ── */}
				<div className="hidden min-h-0 flex-1 flex-col overflow-y-auto lg:flex">
					{selectedItem ? (
						<div className="mx-auto w-full max-w-3xl">
							<div className="sticky top-0 z-10 border-b bg-card px-4 py-3">
								<AsystentItemHeader item={selectedItem} hero bands={marginBands} />
							</div>
							<div className="px-4 py-2 text-[13px]">
								<AsystentRowDetails
									bloz07={selectedItem.bloz07}
									primaryCategoryCode={selectedItem.primaryCategoryCode}
									primaryCategoryLabel={selectedItem.primaryCategoryLabel}
									scope={scope}
									onPickProduct={pickProduct}
								/>
							</div>
						</div>
					) : (
						<div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center text-muted-foreground">
							<Search className="size-6 opacity-40" aria-hidden />
							<p className="text-sm">
								Wybierz produkt z listy, aby zobaczyć cenę, marżę, zamienniki i opis.
							</p>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

/**
 * Nagłówek produktu z regułą kotwicy: nazwa + Nr Kamsoft + sygnały po lewej,
 * Cena prawy-góra, Marża pod ceną. Współdzielony przez wiersz listy (`hero=false`)
 * i nagłówek panelu szczegółów na desktopie (`hero=true`, większy).
 */
function AsystentItemHeader({
	item,
	hero = false,
	bands,
}: {
	item: AsystentItem;
	hero?: boolean;
	bands?: MarginBands;
}) {
	const price = effectiveFinalPriceFromItem(item);
	const signals = pickSignals(item);
	return (
		<div className="flex items-start gap-2">
			<div className="min-w-0 flex-1">
				<div className="flex items-baseline gap-1.5 min-w-0">
					<span
						className={cn(
							"font-medium text-foreground",
							hero ? "text-base" : "truncate text-[15px]",
						)}
						title={item.nazwa}
					>
						{item.nazwa}
					</span>
					{item.dawka && (
						<span className="shrink-0 text-xs text-muted-foreground">{item.dawka}</span>
					)}
				</div>
				{item.primaryCategoryLabel && (
					<div className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
						<Tag className="size-3 shrink-0" aria-hidden />
						<span className="truncate capitalize" title={item.primaryCategoryLabel}>
							{item.primaryCategoryLabel.toLowerCase()}
						</span>
					</div>
				)}
				{/* Badge'y (rodzaj, kontrakt/cennik/zalegacz). "stan" jest w prawej kolumnie. */}
				<div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs">
					<RodzajBadge rodzaj={item.rodzaj} />
					{signals.map((s) => (
						<SignalChip
							key={s.label}
							label={s.label}
							variant={s.variant}
							colorClass={s.colorClass}
						/>
					))}
				</div>
				{/* Nr Kamsoft na samym dole lewej kolumny ("float" — wypelnia przestrzen
				    pod cena/marza/stanem z prawej, wiec nie rosnie wysokosc wiersza). */}
				<div className="mt-1">
					<NrtowChip nrtow={item.nrtow} />
				</div>
			</div>
			<div className="flex shrink-0 flex-col items-end text-right">
				{price != null && (
					<div
						className={cn(
							"font-semibold tabular-nums text-foreground",
							hero ? "text-base" : "text-sm",
						)}
					>
						{price.toFixed(2)} zł
					</div>
				)}
				<div className="mt-0.5">
					<MarginBadge level={marginLevel(item.marginPct, price, bands)} />
				</div>
				<div className="mt-0.5 text-[11px] tabular-nums text-muted-foreground">
					stan {formatStock(item.pharmacyStock)}
				</div>
			</div>
		</div>
	);
}

function AsystentRow({
	item,
	selected,
	onClick,
	onHover,
	bands,
}: {
	item: AsystentItem;
	selected: boolean;
	onClick: () => void;
	onHover?: () => void;
	bands?: MarginBands;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			onMouseEnter={onHover}
			onFocus={onHover}
			className={cn(
				// Pasek --primary jest na wrapperze (spina wiersz + rozwiniecie); tu tylko tlo.
				"w-full px-3 py-2.5 text-left transition-colors",
				selected ? "bg-accent" : "hover:bg-muted/50",
			)}
		>
			<AsystentItemHeader item={item} bands={bands} />
		</button>
	);
}

/// Subtelny klaster łapek (góra/dół) w rogu wiersza — ujawnia się na hover/focus,
/// nie zaśmieca pozycji. Sibling `AsystentRow` (nie wewnątrz jego <button>);
/// stopPropagation, żeby ocena nie rozwijała/zamykała wiersza.
function FeedbackCluster({
	busy,
	pinned,
	onVote,
}: {
	busy: boolean;
	/// Wiersz otwarty → klaster ZOSTAJE widoczny (nie chowa się jak na hover),
	/// żeby ocena "z oryginalnego wiersza" była pod ręką bez dublowania w szczegółach.
	pinned: boolean;
	onVote: (positive: boolean) => void;
}) {
	const btn = (positive: boolean, label: string, Icon: typeof ThumbsUp) => (
		<Tooltip>
			<TooltipTrigger asChild>
				<button
					type="button"
					disabled={busy}
					aria-label={label}
					onClick={(e) => {
						e.stopPropagation();
						onVote(positive);
					}}
					className={cn(
						"inline-flex size-6 items-center justify-center rounded-md text-muted-foreground/60",
						"transition-colors hover:bg-muted disabled:opacity-40",
						positive ? "hover:text-primary" : "hover:text-destructive",
					)}
				>
					<Icon className="size-3.5" />
				</button>
			</TooltipTrigger>
			<TooltipContent side="left">{label}</TooltipContent>
		</Tooltip>
	);
	return (
		<div
			className={cn(
				"absolute bottom-1.5 right-2 flex gap-0.5 transition-opacity focus-within:opacity-100",
				pinned ? "opacity-100" : "opacity-0 group-hover:opacity-100",
			)}
		>
			{btn(true, "Ten produkt pasuje do tego wyszukiwania", ThumbsUp)}
			{btn(false, "Ten produkt nie pasuje do tego wyszukiwania", ThumbsDown)}
		</div>
	);
}

/// Telefon (web): ocena trafności w rozwinięciu wiersza — dotyk nie ma hovera,
/// więc klaster w rogu wiersza byłby niewidoczny, a jego 24 px to za mały cel.
function MobileFeedbackRow({
	busy,
	onVote,
}: {
	busy: boolean;
	onVote: (positive: boolean) => void;
}) {
	const btn = (positive: boolean, label: string, text: string, Icon: typeof ThumbsUp) => (
		<button
			type="button"
			disabled={busy}
			aria-label={label}
			onClick={() => onVote(positive)}
			className={cn(
				"inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-xs text-foreground",
				"transition-colors disabled:opacity-40",
				positive ? "hover:text-primary" : "hover:text-destructive",
			)}
		>
			<Icon className="size-3.5" aria-hidden />
			{text}
		</button>
	);
	return (
		<div className="mb-2 flex flex-wrap items-center gap-2" data-testid="asystent-mobile-feedback">
			<span className="text-muted-foreground">Czy pasuje do wyszukiwania?</span>
			{btn(true, "Ten produkt pasuje do tego wyszukiwania", "Pasuje", ThumbsUp)}
			{btn(false, "Ten produkt nie pasuje do tego wyszukiwania", "Nie pasuje", ThumbsDown)}
		</div>
	);
}

function Centered({ children }: { children: React.ReactNode }) {
	return (
		<div className="flex h-full flex-col items-center justify-center px-4 py-16 text-center text-muted-foreground">
			<p className="text-sm">{children}</p>
		</div>
	);
}
